import React, { useMemo, useState } from 'react';
import GroupResourceTile from './GroupResourceTile';
import GroupResourceListRow from './GroupResourceListRow';
import { usePrefersCoarsePointer } from '../../utils/usePrefersCoarsePointer';
import {
  isSharedAttachmentDrag,
  readSharedAttachmentDragData,
  setSharedAttachmentDragData,
  resolveAttachmentId,
  resolvePayloadAttachmentId,
} from '../../utils/sharedFilesDrag';
import { isDisplayableImage, isLinkAttachment } from '../../utils/fileUtils';

const TILE = 'w-[88px] h-[88px]';

const GroupImageComposer = ({
  attachments = [],
  messageId,
  onReorder,
  onRemove,
  onAddFiles,
  onBrowseAdd,
  onExternalDrop,
  onImageClick,
  onTouchAttachmentDrop,
  onTouchHighlight,
  onSharedDragStart,
  onSharedDragEnd,
  canRename = false,
  onRename,
  renderFileIcon,
  editable = true,
  hint,
  isDropTarget = false,
  tileClassName = TILE,
  showSequence = false,
}) => {
  const coarsePointer = usePrefersCoarsePointer();
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [isFileDragOver, setIsFileDragOver] = useState(false);
  const [isExternalDragOver, setIsExternalDragOver] = useState(false);

  const canDrag = !!(onReorder || onExternalDrop);

  const resetDragUi = () => {
    setDragIndex(null);
    setOverIndex(null);
    setIsFileDragOver(false);
    setIsExternalDragOver(false);
  };

  const clearDragState = () => {
    resetDragUi();
    onSharedDragEnd?.();
  };

  const handleItemDragStart = (e, index) => {
    if (!editable) return;
    if (e.target?.closest?.('button')) {
      e.preventDefault();
      return;
    }
    const attachment = attachments[index];
    setSharedAttachmentDragData(e, {
      sourceMessageId: messageId,
      attachmentId: resolveAttachmentId(attachment),
      fileUrl: attachment?.fileUrl || null,
      itemType: 'attachment',
    });
    e.dataTransfer.setData('text/plain', String(index));
    window.requestAnimationFrame(() => {
      setDragIndex(index);
      onSharedDragStart?.();
    });
  };

  const findAttachmentIndex = (attachmentId) =>
    attachments.findIndex((a) => resolveAttachmentId(a) === attachmentId);

  const tryExternalDrop = (e, insertIndex) => {
    const payload = readSharedAttachmentDragData(e);
    if (!payload || !onExternalDrop) return false;
    const attachmentId = resolvePayloadAttachmentId(payload);
    if (payload.sourceMessageId === messageId && attachmentId) {
      const fromIndex = findAttachmentIndex(attachmentId);
      if (fromIndex >= 0 && fromIndex !== insertIndex && onReorder) {
        onReorder(fromIndex, insertIndex > fromIndex ? insertIndex - 1 : insertIndex);
        return true;
      }
    }
    if (payload.sourceMessageId === messageId && dragIndex !== null && onReorder) {
      const toIndex = insertIndex > dragIndex ? insertIndex - 1 : insertIndex;
      if (dragIndex !== toIndex) {
        onReorder(dragIndex, toIndex);
        return true;
      }
    }
    if (payload.sourceMessageId !== messageId) {
      onExternalDrop(payload, insertIndex);
      return true;
    }
    return false;
  };

  const handleItemDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSharedAttachmentDrag(e)) {
      e.dataTransfer.dropEffect = 'move';
      setIsExternalDragOver(true);
      if (dragIndex !== null && dragIndex !== index) {
        setOverIndex(index);
      } else if (dragIndex === null) {
        setOverIndex(index);
      }
      return;
    }
    e.dataTransfer.dropEffect = 'move';
    if (dragIndex !== null && dragIndex !== index) {
      setOverIndex(index);
    }
  };

  const handleItemDrop = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (tryExternalDrop(e, index)) {
      resetDragUi();
      onSharedDragEnd?.();
      return;
    }
    const from = dragIndex ?? Number(e.dataTransfer.getData('text/plain'));
    if (!Number.isNaN(from) && from !== index && onReorder) {
      onReorder(from, index);
    }
    resetDragUi();
    onSharedDragEnd?.();
  };

  const handleContainerDragOver = (e) => {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.stopPropagation();
      setIsFileDragOver(true);
      return;
    }
    if (isSharedAttachmentDrag(e)) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setIsExternalDragOver(true);
    }
  };

  const handleContainerDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsFileDragOver(false);
      setIsExternalDragOver(false);
    }
  };

  const handleContainerDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files?.length && onAddFiles) {
      onAddFiles(e.dataTransfer.files);
      resetDragUi();
      onSharedDragEnd?.();
      return;
    }
    if (tryExternalDrop(e, attachments.length)) {
      resetDragUi();
      onSharedDragEnd?.();
      return;
    }
    resetDragUi();
    onSharedDragEnd?.();
  };

  const showAdd = editable && (onAddFiles || onBrowseAdd);
  const defaultHint = editable
      ? coarsePointer
        ? 'Tap to open · Long-press to drag · Drop to add'
        : 'Click links to open · Drag to move files · Drop to add files or links'
    : '';
  const resolvedHint = hint === undefined ? defaultHint : hint;
  const highlight = (isFileDragOver && showAdd) || isExternalDragOver || isDropTarget;

  const isGridItem = (attachment) =>
    isDisplayableImage(attachment) && !isLinkAttachment(attachment);

  const { listItems, imageItems } = useMemo(() => {
    const list = [];
    const images = [];
    attachments.forEach((attachment, index) => {
      const entry = { attachment, index };
      if (isGridItem(attachment)) {
        images.push(entry);
      } else {
        list.push(entry);
      }
    });
    return { listItems: list, imageItems: images };
  }, [attachments]);

  const sharedTileProps = (entry) => ({
    attachment: entry.attachment,
    index: entry.index,
    messageId,
    editable,
    canDrag,
    coarsePointer,
    dragIndex,
    overIndex,
    onItemClick: onImageClick,
    onRemove,
    onTouchAttachmentDrop,
    onTouchHighlight,
    canRename,
    onRename,
    renderFileIcon,
    onItemDragStart: handleItemDragStart,
    onItemDragOver: handleItemDragOver,
    onItemDrop: handleItemDrop,
    onDragEnd: clearDragState,
    tileClassName,
    sequenceNumber: showSequence ? entry.index + 1 : undefined,
  });

  return (
    <div
      data-shared-files-drop={messageId}
      data-drop-append="true"
      className={`rounded-lg border-2 border-dashed transition-colors ${
        highlight
          ? 'border-primary-500 bg-primary-50/80 dark:bg-primary-900/20'
          : 'border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/40'
      } p-2`}
      onDragOver={editable ? handleContainerDragOver : undefined}
      onDragLeave={editable ? handleContainerDragLeave : undefined}
      onDrop={editable ? handleContainerDrop : undefined}
    >
      {resolvedHint ? (
        <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-2 px-0.5">
          {resolvedHint}
        </p>
      ) : null}
      <div className="space-y-2">
        {listItems.length > 0 && (
          <div className="space-y-1.5">
            {listItems.map((entry) => (
              <GroupResourceListRow
                key={entry.attachment.id || entry.attachment.fileUrl || entry.index}
                {...sharedTileProps(entry)}
              />
            ))}
          </div>
        )}

        {(imageItems.length > 0 || showAdd) && (
          <div className="flex flex-wrap gap-2">
            {imageItems.map((entry) => (
              <GroupResourceTile
                key={entry.attachment.id || entry.attachment.fileUrl || entry.index}
                {...sharedTileProps(entry)}
              />
            ))}

            {showAdd && (
              <button
                type="button"
                onClick={() => onBrowseAdd?.()}
                className={`${tileClassName} flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-500 text-gray-400 dark:text-gray-500 hover:border-primary-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors`}
                aria-label="Add resources to group"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-[10px] font-medium">Add</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupImageComposer;
